import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import PageLayout from "./PageLayout";
import { mediaUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n).toFixed(2)}`;
const MAX_POLLS = 12;

export default function OrderConfirmationPage() {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // payState: "checking" | "paid" | "failed"
  const [payState, setPayState] = useState(sessionId ? "checking" : "paid");

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;

    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderNumber}`);
        if (active) setOrder(data);
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    const pollPayment = async (attempt = 0) => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setPayState("paid");
          clear();
          fetchOrder();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setPayState("failed");
          setLoading(false);
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (attempt >= MAX_POLLS) {
        if (active) {
          setPayState("checking");
          fetchOrder();
        }
        return;
      }
      setTimeout(() => active && pollPayment(attempt + 1), 2000);
    };

    if (sessionId) {
      pollPayment(0);
    } else {
      fetchOrder();
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, sessionId]);

  const verifying = payState === "checking" && loading;

  return (
    <PageLayout title="Order Confirmed" subtitle={null}>
      <div className="max-w-2xl mx-auto px-5 py-14 md:py-20" data-testid="order-confirmation">
        {payState === "failed" ? (
          <div className="text-center py-16" data-testid="payment-failed">
            <XCircle size={56} className="text-[#e0484b] mx-auto" />
            <h2 className="font-serif-display text-[#222] text-2xl font-bold mt-4">
              Payment not completed
            </h2>
            <p className="font-serif-body text-[#666] mt-2">
              Your payment was cancelled or failed. Nothing has been charged.
            </p>
            <Link
              to="/checkout"
              className="inline-block mt-6 bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body px-6 py-3 rounded-[4px] transition-colors"
            >
              Return to Checkout
            </Link>
          </div>
        ) : verifying ? (
          <div className="flex flex-col items-center py-16 text-[#999]" data-testid="payment-verifying">
            <Loader2 size={32} className="animate-spin" />
            <p className="font-serif-body mt-4">Confirming your payment…</p>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center py-16 text-[#999]">
            <Loader2 size={32} className="animate-spin" />
            <p className="font-serif-body mt-4">Loading your order…</p>
          </div>
        ) : error || !order ? (
          <div className="text-center py-16">
            <p className="font-serif-body text-[#666]">
              We couldn't find that order.
            </p>
            <Link to="/shop" className="inline-block mt-6 font-serif-body text-[#0f9fbc] font-bold">
              ← Back to the shop
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center">
              <CheckCircle2 size={56} className="text-[#41bf2b] mx-auto" />
              <h2 className="font-serif-display text-[#222] text-2xl font-bold mt-4">
                Thank you, {order.customer.name.split(" ")[0]}!
              </h2>
              <p className="font-serif-body text-[#666] mt-2">
                Your order has been placed. A confirmation has been sent to{" "}
                <span className="text-[#333]">{order.customer.email}</span>.
              </p>
              <p className="font-serif-body text-[#0f9fbc] font-bold mt-3">
                Order #{order.order_number}
              </p>
            </div>

            <div className="mt-10 rounded-[10px] border border-[#eaeaea] p-6">
              <div className="space-y-4">
                {order.items.map((i) => (
                  <div key={i.id} className="flex gap-3 items-center">
                    {i.image && (
                      <img
                        src={mediaUrl(i.image)}
                        alt={i.name}
                        className="w-14 h-14 rounded-md object-cover border border-[#eaeaea] shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-serif-body text-[14px] text-[#333] line-clamp-2">
                        {i.name}
                      </p>
                      <p className="font-serif-body text-[12px] text-[#999]">Qty {i.qty}</p>
                    </div>
                    <span className="font-serif-body text-[14px] text-[#333] font-bold">
                      {fmt(i.price * i.qty)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#eaeaea] mt-5 pt-4 space-y-2">
                <Row label="Subtotal" value={fmt(order.subtotal)} />
                <Row label="Shipping" value={order.shipping === 0 ? "Free" : fmt(order.shipping)} />
                {order.tax > 0 && <Row label="Tax" value={fmt(order.tax)} />}
                <div className="flex items-center justify-between pt-2">
                  <span className="font-serif-display text-[#333] text-lg font-bold">Total</span>
                  <span className="font-serif-display text-[#0f9fbc] text-xl font-bold">
                    {fmt(order.amount_total != null ? order.amount_total : order.total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-[10px] bg-[#f4f5f5] p-6">
              <p className="font-serif-body text-[13px] text-[#333] font-bold uppercase tracking-wide mb-2">
                Shipping To
              </p>
              <p className="font-serif-body text-[14px] text-[#555] leading-relaxed">
                {order.customer.name}<br />
                {order.customer.address}<br />
                {order.customer.city}, {order.customer.state} {order.customer.zip}
              </p>
            </div>

            <div className="text-center mt-10">
              <Link
                to="/shop"
                className="inline-block bg-[#0f9fbc] hover:bg-[#008da9] text-white font-serif-body px-6 py-3 rounded-[4px] transition-colors"
              >
                Continue Shopping
              </Link>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-serif-body text-[14px] text-[#666]">{label}</span>
      <span className="font-serif-body text-[14px] text-[#333]">{value}</span>
    </div>
  );
}
